import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { IAccionFormativa, ICreateAccionFormativa, IAccionFormativaErrors, ICursoAccionFormativa, ICreateCursoAccionFormativa, ICursoAccionFormativaErrors, ComunidadAutonoma, COMUNIDADES_AUTONOMAS } from '../types';
import FormHeader from '../components/FormHeader';
import SnackBar from '../components/SnackBar';
import Modal from '../components/Modal';
import { accionFormativaService } from '../services/accionFormativaService';
import { apiUtils } from '../config/api';
import { sociedadStorage } from '../stores/sociedadStorage';
import { accionFormativaStorage } from '../stores/accionFormativaStorage';
import './accion-formativa-form.css';

/**
 * Parsea una fecha desde la API evitando problemas de zona horaria
 * @param dateString - String de fecha en formato ISO (ej: "2025-11-17T23:00:00.000Z")
 * @returns Date object con la fecha correcta en hora local
 * 
 * IMPORTANTE: La API devuelve fechas en UTC. Cuando se parsea, JavaScript las convierte
 * automáticamente a hora local. Por ejemplo:
 * - "2025-11-22T23:00:00.000Z" (UTC) → 2025-11-23 00:00:00 (GMT+1)
 * - Necesitamos extraer la fecha en hora local, no la fecha UTC
 */
const parseDateFromAPI = (dateString: string | null | undefined): Date => {
  if (!dateString) return new Date();

  // Parsear el string ISO completo (JavaScript lo convierte automáticamente a hora local)
  const dateInLocal = new Date(dateString);

  // Extraer año, mes y día de la fecha en hora local (no UTC)
  const year = dateInLocal.getFullYear();
  const month = dateInLocal.getMonth();
  const day = dateInLocal.getDate();

  // Crear una nueva fecha con esos valores en hora local (sin hora, solo fecha)
  return new Date(year, month, day);
};

/**
 * Formatea una fecha a formato YYYY-MM-DD sin conversión de zona horaria
 * @param date - Date object
 * @returns String en formato YYYY-MM-DD
 */
const formatDateForAPI = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

interface AccionFormativaFormProps {
  sociedadId: number;
  onBack: () => void;
  onComplete: (accionFormativa: IAccionFormativa, cursos: ICursoAccionFormativa[]) => void;
  onError?: (error: string) => void;
  onNavigateToStart?: () => void;
}

/**
 * @fileoverview Componente de formulario para crear una acción formativa
 * @description Permite al usuario crear una acción formativa y sus cursos asociados
 */

const AccionFormativaForm: React.FC<AccionFormativaFormProps> = ({
  sociedadId,
  onBack,
  onComplete,
  onError,
  onNavigateToStart
}) => {
  const [formData, setFormData] = useState<ICreateAccionFormativa>({
    nombre: null,
    expediente: '',
    administracionPublica: null,
    numAlumnos: 0,
    numHoras: null,
    fechaInicio: new Date(), // Se ajustará en useEffect si es necesario
    fechaFin: new Date(), // Se ajustará en useEffect si es necesario
    ccaa: ComunidadAutonoma.CASTILLA_Y_LEON,
    ubicacion: null,
    practicas: null,
    modalidad: null,
    cursosText: null,
    idSociedad: sociedadId,
  });


  const [cursos, setCursos] = useState<ICreateCursoAccionFormativa[]>([]);
  const [errors, setErrors] = useState<IAccionFormativaErrors>({});
  const [cursoErrors, setCursoErrors] = useState<{ [key: number]: ICursoAccionFormativaErrors }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState<'success' | 'warning'>('success');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [accionFormativaId, setAccionFormativaId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Obtiene el ID de la sociedad del LocalStorage al cargar el componente
   * y carga los datos guardados del formulario si existen
   * Si hay un ID de acción formativa guardado, carga los datos desde la API
   */
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      // Verificar si hay un ID guardado
      const storedId = accionFormativaStorage.getAccionFormativaId();

      if (storedId) {
        try {
          // Cargar datos desde la API
          const response = await accionFormativaService.obtenerAccionFormativa(storedId);

          if (response.success && response.data) {
            // La respuesta tiene la estructura: response.data.accionFormativa
            const accionFormativaData = response.data.accionFormativa || response.data;
            const cursosData = response.data.cursos || response.data.cursoAccionFormativa || [];

            // Establecer el ID INMEDIATAMENTE después de verificar la respuesta (igual que CursoForm)
            setAccionFormativaId(storedId);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Parsear fechas desde la API evitando problemas de zona horaria
            const fechaInicioParsed = parseDateFromAPI(accionFormativaData.fechaInicio);
            const fechaFinParsed = parseDateFromAPI(accionFormativaData.fechaFin);

            // Ajustar fechas si son anteriores a hoy
            const fechaInicio = fechaInicioParsed < today ? today : fechaInicioParsed;
            const fechaFin = fechaFinParsed < today ? today : fechaFinParsed;

            // Obtener el ID de sociedad del LocalStorage o usar el de la API
            const storedSociedadId = sociedadStorage.getSociedadId();
            const finalSociedadId = storedSociedadId || accionFormativaData.idSociedad || sociedadId;

            // Cargar datos de la acción formativa (igual estructura que CursoForm)
            setFormData({
              nombre: accionFormativaData.nombre ?? null,
              expediente: accionFormativaData.expediente || '',
              administracionPublica: accionFormativaData.administracionPublica ?? null,
              numAlumnos: accionFormativaData.numAlumnos || 0,
              numHoras: accionFormativaData.numHoras ?? null,
              fechaInicio: fechaInicio,
              fechaFin: fechaFin,
              ccaa: accionFormativaData.ccaa || ComunidadAutonoma.CASTILLA_Y_LEON,
              ubicacion: accionFormativaData.ubicacion ?? accionFormativaData.codigoPostal ?? null,
              practicas: accionFormativaData.practicas ?? null,
              modalidad: accionFormativaData.modalidad ?? null,
              cursosText: accionFormativaData.cursosText ?? null,
              idSociedad: finalSociedadId
            });

            // Cargar cursos si existen
            if (cursosData && Array.isArray(cursosData) && cursosData.length > 0) {
              const cursosAjustados = cursosData.map((curso: any) => {
                const cursoFechaInicioParsed = curso.fechaInicio ? parseDateFromAPI(curso.fechaInicio) : null;
                const cursoFechaFinParsed = curso.fechaFin ? parseDateFromAPI(curso.fechaFin) : null;

                const cursoFechaInicio = curso.fechaInicio && cursoFechaInicioParsed && cursoFechaInicioParsed < today
                  ? today
                  : (cursoFechaInicioParsed || null);
                const cursoFechaFin = curso.fechaFin && cursoFechaFinParsed && cursoFechaFinParsed < today
                  ? today
                  : (cursoFechaFinParsed || null);

                return {
                  idCursoAccionFormativa: curso.idCursoAccionFormativa,
                  nombre: curso.nombre || '',
                  expediente: curso.expediente ?? null,
                  modalidad: curso.modalidad ?? null,
                  horas: curso.horas ?? null,
                  numAlumnos: curso.numAlumnos?.toString() || '',
                  fechaInicio: cursoFechaInicio,
                  fechaFin: cursoFechaFin,
                  ubicacion: curso.ubicacion ?? null,
                  practicas: curso.practicas ?? null,
                  accionFormativaIdAccionFormativa: storedId
                };
              });
              setCursos(cursosAjustados);
            } else {
              setCursos([]);
            }

            // No guardar datos en localStorage cuando hay ID, solo el ID ya está guardado
            // Los datos se cargan siempre desde la API cuando hay ID

            setIsLoading(false);
            return;
          }
        } catch (error) {
          // Si hay error al cargar, continuar con el flujo normal
          accionFormativaStorage.clearAccionFormativaId();
        }
      }

      // Flujo normal: cargar desde localStorage (solo si no hay ID)
      setIsLoading(false);
    };

    loadData();
  }, [sociedadId]);

  /**
   * Carga datos desde localStorage si no hay ID guardado
   */
  useEffect(() => {
    // No ejecutar si está cargando o si ya hay un ID (modo edición) - igual que CursoForm
    if (isLoading || accionFormativaId) return;

    const storedSociedadId = sociedadStorage.getSociedadId();
    if (storedSociedadId && storedSociedadId !== sociedadId) {
      setFormData(prev => ({
        ...prev,
        idSociedad: storedSociedadId
      }));
    }

    // No cargar datos completos desde LocalStorage, solo el ID se guarda
    // Los datos se cargan desde la API cuando hay un ID guardado

    const storedCursos = accionFormativaStorage.getCursos();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Asegurar que las fechas iniciales no sean anteriores a hoy
    setFormData(prev => {
      const fechaInicio = prev.fechaInicio < today ? today : prev.fechaInicio;
      const fechaFin = prev.fechaFin < today ? today : prev.fechaFin;
      return {
        ...prev,
        fechaInicio,
        fechaFin
      };
    });

    if (storedCursos && storedCursos.length > 0) {
      // Asegurar que las fechas de los cursos no sean anteriores a hoy
      const cursosAjustados = storedCursos.map(curso => {
        if (curso.fechaInicio && curso.fechaInicio < today) {
          return { ...curso, fechaInicio: today };
        }
        if (curso.fechaFin && curso.fechaFin < today) {
          return { ...curso, fechaFin: today };
        }
        return curso;
      });
      setCursos(cursosAjustados);
    }
  }, [sociedadId, isLoading, accionFormativaId]);


  /**
   * Maneja cambios en los inputs y actualiza el estado del formulario
   * @param e - Evento de cambio del input
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    let newValue: any;
    if (type === 'number') {
      newValue = value === '' ? null : Number(value);
    } else if (name === 'ccaa') {
      newValue = value as ComunidadAutonoma;
    } else if (name === 'practicas' || name === 'modalidad') {
      // Para prácticas y modalidad, si está vacío, usar null
      newValue = value && value.trim() !== '' ? value : null;
    } else if (name === 'nombre' || name === 'administracionPublica' || name === 'ubicacion') {
      // Para campos opcionales, si está vacío, usar null
      newValue = value;
    } else {
      newValue = value;
    }

    const updatedData = {
      ...formData,
      [name]: newValue
    };

    setFormData(updatedData);

    // No guardar datos completos en LocalStorage, solo el ID cuando se crea

    // Clear error when user starts typing
    if (errors[name as keyof IAccionFormativaErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  /**
   * Maneja cambios en los inputs de fecha
   * @param e - Evento de cambio del input
   */
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Parsear la fecha desde el input (formato YYYY-MM-DD) evitando problemas de zona horaria
    // El input date siempre devuelve el formato YYYY-MM-DD
    const selectedDate = parseDateFromAPI(value);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Validar que la fecha no sea anterior a la fecha actual
    if (selectedDate < today) {
      setErrors(prev => ({
        ...prev,
        [name]: `La fecha ${name === 'fechaInicio' ? 'de inicio' : 'de fin'} no puede ser anterior a la fecha actual`
      }));
      return;
    }

    const updatedData = {
      ...formData,
      [name]: selectedDate
    };

    setFormData(updatedData);

    // No guardar datos completos en LocalStorage, solo el ID cuando se crea

    // Clear error when user changes date
    if (errors[name as keyof IAccionFormativaErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  /**
   * Maneja cambios en el editor Quill
   * @param content - Contenido HTML del editor
   */
  const handleEditorChange = (content: string) => {
    const updatedData = {
      ...formData,
      cursosText: content || null
    };

    setFormData(updatedData);

    // No guardar datos completos en LocalStorage, solo el ID cuando se crea
  };

  /**
   * Agrega un nuevo curso a la lista
   */
  const addCurso = () => {
    const nuevoCurso: ICreateCursoAccionFormativa = {
      nombre: '',
      expediente: null,
      modalidad: null,
      horas: 0,
      numAlumnos: '',
      fechaInicio: null,
      fechaFin: null,
      ubicacion: null,
      practicas: null,
      accionFormativaIdAccionFormativa: 0 // Se asignará cuando se cree la acción formativa
    };
    const updatedCursos = [...cursos, nuevoCurso];
    setCursos(updatedCursos);

    // Guardar en LocalStorage solo si NO hay ID (modo creación)
    // Si hay ID (modo edición), los datos se cargan desde la API
    if (!accionFormativaId) {
      accionFormativaStorage.saveCursos(updatedCursos);
    }
  };

  /**
   * Elimina un curso de la lista
   * @param index - Índice del curso a eliminar
   */
  const removeCurso = (index: number) => {
    const updatedCursos = cursos.filter((_, i) => i !== index);
    setCursos(updatedCursos);

    // Guardar en LocalStorage solo si NO hay ID (modo creación)
    // Si hay ID (modo edición), los datos se cargan desde la API
    if (!accionFormativaId) {
      accionFormativaStorage.saveCursos(updatedCursos);
    }

    // Limpiar errores del curso eliminado
    setCursoErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  /**
   * Maneja cambios en los inputs de los cursos
   * @param e - Evento de cambio del input
   * @param index - Índice del curso
   */
  const handleCursoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, index: number) => {
    const { name, value, type } = e.target;

    let newValue: any;
    if (type === 'date') {
      if (value) {
        // Parsear la fecha desde el input (formato YYYY-MM-DD) evitando problemas de zona horaria
        const selectedDate = parseDateFromAPI(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Validar que la fecha no sea anterior a la fecha actual
        if (selectedDate < today) {
          setCursoErrors(prev => ({
            ...prev,
            [index]: {
              ...prev[index],
              [name]: `La fecha ${name === 'fechaInicio' ? 'de inicio' : 'de fin'} no puede ser anterior a la fecha actual`
            }
          }));
          return;
        }
        newValue = selectedDate;
      } else {
        newValue = null;
      }
    } else if (type === 'number') {
      newValue = value === '' ? null : Number(value);
    } else if (name === 'practicas') {
      // Para prácticas, si el valor está vacío, usar null
      newValue = value && value.trim() !== '' ? value : null;
    } else if (name === 'modalidad') {
      // Para modalidad, si está vacío, usar null
      newValue = value && value.trim() !== '' ? value : null;
    } else if (name === 'ubicacion') {
      // Para ubicación, si está vacío, usar null
      newValue = value;
    } else if (name === 'expediente') {
      // Para expediente, si está vacío, usar null (opcional)
      newValue = value;
    } else {
      newValue = value;
    }

    const updatedCursos = cursos.map((curso, i) => {
      if (i !== index) return curso;
      return {
        ...curso,
        [name]: newValue
      };
    });

    setCursos(updatedCursos);

    // Guardar en LocalStorage solo si NO hay ID (modo creación)
    // Si hay ID (modo edición), los datos se cargan desde la API
    if (!accionFormativaId) {
      accionFormativaStorage.saveCursos(updatedCursos);
    }

    // Clear error when user starts typing
    if (cursoErrors[index]?.[name as keyof ICursoAccionFormativaErrors]) {
      setCursoErrors(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          [name]: undefined
        }
      }));
    }
  };

  /**
   * Valida los datos del formulario
   * @returns boolean - true si la validación es exitosa
   */
  const validateForm = (): boolean => {
    const newErrors: IAccionFormativaErrors = {};
    const newCursoErrors: { [key: number]: ICursoAccionFormativaErrors } = {};

    // Validación de nombre (opcional, no se valida)

    // Validación de expediente
    if (!formData.expediente.trim()) {
      newErrors.expediente = 'El expediente es obligatorio';
    }

    // Validación de administración pública (opcional, no se valida)

    // Validación de número de alumnos
    if (formData.numAlumnos <= 0) {
      newErrors.numAlumnos = 'El número de alumnos debe ser mayor a 0';
    }

    // Validación de número de horas (opcional, no se valida)

    // Validación de fechas
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (!formData.fechaInicio) {
      newErrors.fechaInicio = 'La fecha de inicio es obligatoria';
    } else {
      if (formData.fechaInicio < today) {
        newErrors.fechaInicio = 'La fecha de inicio no puede ser anterior a la fecha actual';
      }
    }

    if (!formData.fechaFin) {
      newErrors.fechaFin = 'La fecha de fin es obligatoria';
    } else {
      if (formData.fechaFin < today) {
        newErrors.fechaFin = 'La fecha de fin no puede ser anterior a la fecha actual';
      } else if (formData.fechaInicio && formData.fechaFin <= formData.fechaInicio) {
        newErrors.fechaFin = 'La fecha de fin debe ser posterior a la fecha de inicio';
      }
    }

    // Validación de CCAA (obligatorio)
    if (!formData.ccaa) {
      newErrors.ccaa = 'La Comunidad Autónoma es obligatoria';
    }

    // Validación de ubicación (opcional, pero si se proporciona debe ser válido)
    if (formData.ubicacion && formData.ubicacion.length > 100) {
      newErrors.ubicacion = 'La ubicación no puede exceder los 100 caracteres';
    }

    // Validación de prácticas (opcional, no se valida)

    // Validación de modalidad (opcional, no se valida)

    // Validación de cursos - Si no hay cursos, retornar false para mostrar modal
    if (cursos.length === 0) {
      // No agregar error, se manejará mostrando modal
    } else {
      cursos.forEach((curso, index) => {
        const cursoError: ICursoAccionFormativaErrors = {};

        if (!curso.nombre.trim()) {
          cursoError.nombre = 'El nombre del curso es obligatorio';
        }

        if (!curso.numAlumnos || curso.numAlumnos.toString().trim() === '') {
          cursoError.numAlumnos = 'El número de alumnos es obligatorio';
        } else if (isNaN(Number(curso.numAlumnos)) || Number(curso.numAlumnos) <= 0) {
          cursoError.numAlumnos = 'El número de alumnos debe ser un número válido mayor a 0';
        }

        // Validación de expediente (opcional, no se valida)

        // Validación de fecha de inicio (opcional)
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (curso.fechaInicio) {
          if (curso.fechaInicio < today) {
            cursoError.fechaInicio = 'La fecha de inicio no puede ser anterior a la fecha actual';
          } else if (curso.fechaFin && curso.fechaInicio >= curso.fechaFin) {
            cursoError.fechaInicio = 'La fecha de inicio debe ser anterior a la fecha de fin';
          }
        }

        // Validación de fecha de fin (opcional)
        if (curso.fechaFin) {
          if (curso.fechaFin < today) {
            cursoError.fechaFin = 'La fecha de fin no puede ser anterior a la fecha actual';
          } else if (curso.fechaInicio && curso.fechaFin <= curso.fechaInicio) {
            cursoError.fechaFin = 'La fecha de fin debe ser posterior a la fecha de inicio';
          }
        }

        // Validación de ubicación (opcional, no se valida)

        // Validación de prácticas (opcional, no se valida)

        if (Object.keys(cursoError).length > 0) {
          newCursoErrors[index] = cursoError;
        }
      });
    }

    setErrors(newErrors);
    setCursoErrors(newCursoErrors);
    return Object.keys(newErrors).length === 0 && Object.keys(newCursoErrors).length === 0;
  };

  /**
   * Maneja el envío del formulario
   * @param e - Evento de envío del formulario
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validar que haya cursosText con contenido real o al menos un curso
    const cursosTextHasContent = formData.cursosText &&
      formData.cursosText.trim() !== '' &&
      formData.cursosText.trim() !== '<p><br></p>' &&
      formData.cursosText.trim() !== '<p><br /></p>';

    if (!cursosTextHasContent && cursos.length === 0) {
      setErrorMessage('Debe agregar contenido en el editor de cursos o al menos un curso asociado a la acción formativa');
      setShowErrorModal(true);
      return;
    }

    if (validateForm()) {
      setIsSubmitting(true);

      try {
        // Preparar datos para la API en el formato correcto
        const apiData = {
          nombre: formData.nombre,
          expediente: formData.expediente,
          administracionPublica: formData.administracionPublica,
          numAlumnos: formData.numAlumnos,
          numHoras: formData.numHoras,
          fechaInicio: formatDateForAPI(formData.fechaInicio), // Formato YYYY-MM-DD sin conversión de zona horaria
          fechaFin: formatDateForAPI(formData.fechaFin), // Formato YYYY-MM-DD sin conversión de zona horaria
          ccaa: formData.ccaa,
          ubicacion: formData.ubicacion,
          practicas: formData.practicas,
          modalidad: formData.modalidad,
          cursosText: formData.cursosText,
          idSociedad: formData.idSociedad,
          cursoAccionFormativa: cursos.map(curso => ({
            ...(curso.idCursoAccionFormativa && { idCursoAccionFormativa: curso.idCursoAccionFormativa }),
            nombre: curso.nombre,
            expediente: curso.expediente || null,
            modalidad: curso.modalidad || null,
            horas: curso.horas,
            numAlumnos: Number(curso.numAlumnos),
            fechaInicio: curso.fechaInicio ? formatDateForAPI(curso.fechaInicio) : null,
            fechaFin: curso.fechaFin ? formatDateForAPI(curso.fechaFin) : null,
            ubicacion: curso.ubicacion || null,
            practicas: curso.practicas || null
          }))
        };

        let response;
        let accionFormativa: IAccionFormativa;
        let cursosCreados: ICursoAccionFormativa[];

        // Si hay un ID, actualizar; si no, crear
        if (accionFormativaId) {
          response = await accionFormativaService.actualizarAccionFormativa(accionFormativaId, apiData);

          if (response.success) {
            setSuccessMessage(response.message || 'Acción formativa actualizada exitosamente');
            setSnackbarType('warning');
            setShowSuccessSnackbar(true);

            // Crear objetos para el callback con datos actualizados
            accionFormativa = {
              idAccionFormativa: accionFormativaId,
              ...formData
            };

            cursosCreados = cursos.map((curso) => ({
              idCursoAccionFormativa: curso.idCursoAccionFormativa || Math.floor(Math.random() * 1000),
              nombre: curso.nombre,
              expediente: curso.expediente,
              modalidad: curso.modalidad,
              horas: curso.horas,
              numAlumnos: curso.numAlumnos,
              fechaInicio: curso.fechaInicio,
              fechaFin: curso.fechaFin,
              ubicacion: curso.ubicacion,
              practicas: curso.practicas,
              accionFormativaIdAccionFormativa: accionFormativaId
            }));
          } else {
            setErrorMessage(response.message || 'Error al actualizar la acción formativa');
            setShowErrorModal(true);
            return;
          }
        } else {
          // Crear nueva acción formativa
          response = await accionFormativaService.crearAccionFormativa(apiData);


          if (response.success) {
            // La respuesta tiene la estructura: response.data.accionFormativa.idAccionFormativa
            const newId = response.data?.accionFormativa?.idAccionFormativa;


            if (newId) {
              // Guardar el ID en el store
              accionFormativaStorage.saveAccionFormativaId(newId);
              setAccionFormativaId(newId);

              // Verificar que se guardó correctamente
              const storedId = accionFormativaStorage.getAccionFormativaId();
            }

            setSuccessMessage(response.message || 'Acción formativa creada exitosamente');
            setSnackbarType('success');
            setShowSuccessSnackbar(true);

            // Crear objetos para el callback
            accionFormativa = {
              idAccionFormativa: newId || Math.floor(Math.random() * 1000),
              ...formData
            };

            cursosCreados = cursos.map((curso, index) => ({
              idCursoAccionFormativa: Math.floor(Math.random() * 1000) + index,
              nombre: curso.nombre,
              expediente: curso.expediente,
              modalidad: curso.modalidad,
              horas: curso.horas,
              numAlumnos: curso.numAlumnos,
              fechaInicio: curso.fechaInicio,
              fechaFin: curso.fechaFin,
              ubicacion: curso.ubicacion,
              practicas: curso.practicas,
              accionFormativaIdAccionFormativa: accionFormativa.idAccionFormativa
            }));
          } else {
            setErrorMessage(response.message || 'Error al crear la acción formativa');
            setShowErrorModal(true);
            return;
          }
        }

        // No limpiar el store si estamos en modo edición, para permitir volver atrás
        if (!accionFormativaId) {
          // Solo limpiar si es creación nueva y se completa el flujo
          // accionFormativaStorage.clearAccionFormativa();
        }

        setTimeout(() => {
          onComplete(accionFormativa, cursosCreados);
        }, 2000);

      } catch (error) {
        const errorMsg = apiUtils.handleError(error);
        setErrorMessage(errorMsg);
        setShowErrorModal(true);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="container">
      <div className="form-container">
        <FormHeader onLogoClick={onNavigateToStart} />
        <h2 className="form-subtitle">ACCIÓN FORMATIVA</h2>

        {accionFormativaId && (
          <div className="update-mode-notice">
            <p>
              <strong>Modo de actualización</strong>
            </p>
            <p>
              Está actualizando una acción formativa existente.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="nombre">
              Nombre de la acción formativa
            </label>
            <input
              type="text"
              id="nombre"
              name="nombre"
              value={formData.nombre || ''}
              onChange={handleInputChange}
              className={errors.nombre ? 'error' : ''}
              aria-describedby={errors.nombre ? 'nombre-error' : undefined}
            />
            {errors.nombre && <span id="nombre-error" className="error-message">{errors.nombre}</span>}
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="expediente">
                Número de expediente <span className="required">*</span>
              </label>
              <input
                type="text"
                id="expediente"
                name="expediente"
                value={formData.expediente}
                onChange={handleInputChange}
                className={errors.expediente ? 'error' : ''}
                required
                aria-describedby={errors.expediente ? 'expediente-error' : undefined}
              />
              {errors.expediente && <span id="expediente-error" className="error-message">{errors.expediente}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="administracionPublica">
                Administración pública
              </label>
              <input
                type="text"
                id="administracionPublica"
                name="administracionPublica"
                value={formData.administracionPublica || ''}
                onChange={handleInputChange}
                className={errors.administracionPublica ? 'error' : ''}
                aria-describedby={errors.administracionPublica ? 'administracionPublica-error' : undefined}
              />
              {errors.administracionPublica && <span id="administracionPublica-error" className="error-message">{errors.administracionPublica}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="numAlumnos">
                Número de Alumnos <span className="required">*</span>
              </label>
              <input
                type="number"
                id="numAlumnos"
                name="numAlumnos"
                value={formData.numAlumnos || ''}
                onChange={handleInputChange}
                className={errors.numAlumnos ? 'error' : ''}
                required
                min="1"
                aria-describedby={errors.numAlumnos ? 'numAlumnos-error' : undefined}
              />
              {errors.numAlumnos && <span id="numAlumnos-error" className="error-message">{errors.numAlumnos}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="numHoras">
                Número de horas
              </label>
              <input
                type="number"
                id="numHoras"
                name="numHoras"
                value={formData.numHoras || ''}
                onChange={handleInputChange}
                className={errors.numHoras ? 'error' : ''}
                min="0"
                aria-describedby={errors.numHoras ? 'numHoras-error' : undefined}
              />
              {errors.numHoras && <span id="numHoras-error" className="error-message">{errors.numHoras}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="fechaInicio">
                Fecha de inicio <span className="required">*</span>
              </label>
              <input
                type="date"
                id="fechaInicio"
                name="fechaInicio"
                value={formatDateForAPI(formData.fechaInicio)}
                onChange={handleDateChange}
                className={errors.fechaInicio ? 'error' : ''}
                required
                min={formatDateForAPI(new Date())}
                aria-describedby={errors.fechaInicio ? 'fechaInicio-error' : undefined}
              />
              {errors.fechaInicio && <span id="fechaInicio-error" className="error-message">{errors.fechaInicio}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="fechaFin">
                Fecha de fin <span className="required">*</span>
              </label>
              <input
                type="date"
                id="fechaFin"
                name="fechaFin"
                value={formatDateForAPI(formData.fechaFin)}
                onChange={handleDateChange}
                className={errors.fechaFin ? 'error' : ''}
                required
                min={formatDateForAPI(new Date())}
                aria-describedby={errors.fechaFin ? 'fechaFin-error' : undefined}
              />
              {errors.fechaFin && <span id="fechaFin-error" className="error-message">{errors.fechaFin}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="ccaa">
                Comunidad Autónoma donde se imparte la formación  <span className="required">*</span>
              </label>
              <select
                id="ccaa"
                name="ccaa"
                value={formData.ccaa}
                onChange={handleInputChange}
                className={errors.ccaa ? 'error' : ''}
                required
                aria-describedby={errors.ccaa ? 'ccaa-error' : undefined}
              >
                {COMUNIDADES_AUTONOMAS.map(ccaa => (
                  <option key={ccaa} value={ccaa}>{ccaa}</option>
                ))}
              </select>
              {errors.ccaa && <span id="ccaa-error" className="error-message">{errors.ccaa}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="ubicacion">
                Ubicación de los cursos
              </label>
              <input
                type="text"
                id="ubicacion"
                name="ubicacion"
                value={formData.ubicacion || ''}
                onChange={handleInputChange}
                className={errors.ubicacion ? 'error' : ''}
                maxLength={100}
                aria-describedby={errors.ubicacion ? 'ubicacion-error' : undefined}
              />
              {errors.ubicacion && <span id="ubicacion-error" className="error-message">{errors.ubicacion}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group form-group-media">
              <label htmlFor="practicas">
                Prácticas
              </label>
              <select
                id="practicas"
                name="practicas"
                value={formData.practicas || ''}
                onChange={handleInputChange}
                className={errors.practicas ? 'error' : ''}
                aria-describedby={errors.practicas ? 'practicas-error' : undefined}
              >
                <option value=""></option>
                <option value="No">No</option>
                <option value="Si">Sí</option>
              </select>
              {errors.practicas && <span id="practicas-error" className="error-message">{errors.practicas}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="modalidad">
                Modalidad
              </label>
              <select
                id="modalidad"
                name="modalidad"
                value={formData.modalidad || ''}
                onChange={handleInputChange}
                className={errors.modalidad ? 'error' : ''}
                aria-describedby={errors.modalidad ? 'modalidad-error' : undefined}
              >
                <option value=""></option>
                <option value="Presencial">Presencial</option>
                <option value="Teleformación">Teleformación</option>
              </select>
              {errors.modalidad && <span id="modalidad-error" className="error-message">{errors.modalidad}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="cursosText">
              Rellenar los cursos asociados a la acción formativa
            </label>
            <ReactQuill
              theme="snow"
              value={formData.cursosText || ''}
              onChange={handleEditorChange}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline'],
                  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                ]
              }}
              formats={[
                'header',
                'bold', 'italic', 'underline',
                'list', 'bullet',
              ]}
            />
            {errors.cursosText && <span className="error-message">{errors.cursosText}</span>}
          </div>

          {/* Sección de Cursos */}
          <div className="form-section">
            <div className="form-section-header">
              <h3>Registrar cursos de la acción formativa de forma individual</h3>
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={addCurso}
              >
                + Agregar Curso
              </button>
            </div>

            {cursos.length === 0 && (
              <div className="empty-state">
                <p>No hay cursos agregados. Haz clic en "Agregar Curso" para comenzar.</p>
              </div>
            )}

            {cursos.map((curso, index) => (
              <div key={index} className="curso-card">
                <div className="curso-header">
                  <h4>Curso {index + 1}</h4>
                  <button
                    type="button"
                    className="btn btn-danger btn-small"
                    onClick={() => removeCurso(index)}
                  >
                    Eliminar
                  </button>
                </div>

                <div className="form-row">
                  <div className="form-group form-group-large">
                    <label htmlFor={`curso-nombre-${index}`}>
                      Nombre del curso <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id={`curso-nombre-${index}`}
                      name="nombre"
                      value={curso.nombre}
                      onChange={(e) => handleCursoChange(e, index)}
                      className={cursoErrors[index]?.nombre ? 'error' : ''}
                      required
                      aria-describedby={cursoErrors[index]?.nombre ? `curso-nombre-${index}-error` : undefined}
                    />
                    {cursoErrors[index]?.nombre && (
                      <span id={`curso-nombre-${index}-error`} className="error-message">
                        {cursoErrors[index]?.nombre}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group form-group-large">
                    <label htmlFor={`curso-expediente-${index}`}>
                      Expediente
                    </label>
                    <input
                      type="text"
                      id={`curso-expediente-${index}`}
                      name="expediente"
                      value={curso.expediente || ''}
                      onChange={(e) => handleCursoChange(e, index)}
                      className={cursoErrors[index]?.expediente ? 'error' : ''}
                      aria-describedby={cursoErrors[index]?.expediente ? `curso-expediente-${index}-error` : undefined}
                    />
                    {cursoErrors[index]?.expediente && (
                      <span id={`curso-expediente-${index}-error`} className="error-message">
                        {cursoErrors[index]?.expediente}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group form-group-media">
                    <label htmlFor={`curso-numAlumnos-${index}`}>
                      Número de alumnos <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      id={`curso-numAlumnos-${index}`}
                      name="numAlumnos"
                      value={curso.numAlumnos}
                      onChange={(e) => handleCursoChange(e, index)}
                      className={cursoErrors[index]?.numAlumnos ? 'error' : ''}
                      required
                      min="1"
                      aria-describedby={cursoErrors[index]?.numAlumnos ? `curso-numAlumnos-${index}-error` : undefined}
                    />
                    {cursoErrors[index]?.numAlumnos && (
                      <span id={`curso-numAlumnos-${index}-error`} className="error-message">
                        {cursoErrors[index]?.numAlumnos}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor={`curso-horas-${index}`}>
                      Número de horas
                    </label>
                    <input
                      type="number"
                      id={`curso-horas-${index}`}
                      name="horas"
                      value={curso.horas || ''}
                      onChange={(e) => handleCursoChange(e, index)}
                      className={cursoErrors[index]?.horas ? 'error' : ''}
                      min="0"
                      aria-describedby={cursoErrors[index]?.horas ? `curso-horas-${index}-error` : undefined}
                    />
                    {cursoErrors[index]?.horas && (
                      <span id={`curso-horas-${index}-error`} className="error-message">
                        {cursoErrors[index]?.horas}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group form-group-media">
                    <label htmlFor={`curso-fechaInicio-${index}`}>
                      Fecha de inicio
                    </label>
                    <input
                      type="date"
                      id={`curso-fechaInicio-${index}`}
                      name="fechaInicio"
                      value={curso.fechaInicio ? formatDateForAPI(curso.fechaInicio) : ''}
                      onChange={(e) => handleCursoChange(e, index)}
                      className={cursoErrors[index]?.fechaInicio ? 'error' : ''}
                      min={formatDateForAPI(new Date())}
                      aria-describedby={cursoErrors[index]?.fechaInicio ? `curso-fechaInicio-${index}-error` : undefined}
                    />
                    {cursoErrors[index]?.fechaInicio && (
                      <span id={`curso-fechaInicio-${index}-error`} className="error-message">
                        {cursoErrors[index]?.fechaInicio}
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor={`curso-fechaFin-${index}`}>
                      Fecha de fin
                    </label>
                    <input
                      type="date"
                      id={`curso-fechaFin-${index}`}
                      name="fechaFin"
                      value={curso.fechaFin ? formatDateForAPI(curso.fechaFin) : ''}
                      onChange={(e) => handleCursoChange(e, index)}
                      className={cursoErrors[index]?.fechaFin ? 'error' : ''}
                      min={formatDateForAPI(new Date())}
                      aria-describedby={cursoErrors[index]?.fechaFin ? `curso-fechaFin-${index}-error` : undefined}
                    />
                    {cursoErrors[index]?.fechaFin && (
                      <span id={`curso-fechaFin-${index}-error`} className="error-message">
                        {cursoErrors[index]?.fechaFin}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group form-group-large">
                    <label htmlFor={`curso-ubicacion-${index}`}>
                      Ubicación
                    </label>
                    <input
                      type="text"
                      id={`curso-ubicacion-${index}`}
                      name="ubicacion"
                      value={curso.ubicacion || ''}
                      onChange={(e) => handleCursoChange(e, index)}
                      className={cursoErrors[index]?.ubicacion ? 'error' : ''}
                      aria-describedby={cursoErrors[index]?.ubicacion ? `curso-ubicacion-${index}-error` : undefined}
                    />
                    {cursoErrors[index]?.ubicacion && (
                      <span id={`curso-ubicacion-${index}-error`} className="error-message">
                        {cursoErrors[index]?.ubicacion}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group form-group-media">
                    <label htmlFor={`curso-modalidad-${index}`}>
                      Modalidad
                    </label>
                    <select
                      id={`curso-modalidad-${index}`}
                      name="modalidad"
                      value={curso.modalidad || ''}
                      onChange={(e) => handleCursoChange(e, index)}
                      className={cursoErrors[index]?.modalidad ? 'error' : ''}
                      aria-describedby={cursoErrors[index]?.modalidad ? `curso-modalidad-${index}-error` : undefined}
                    >
                      <option value=""></option>
                      <option value="Presencial">Presencial</option>
                      <option value="Teleformación">Teleformación</option>
                    </select>
                    {cursoErrors[index]?.modalidad && (
                      <span id={`curso-modalidad-${index}-error`} className="error-message">
                        {cursoErrors[index]?.modalidad}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor={`curso-practicas-${index}`}>
                      Prácticas
                    </label>
                    <select
                      id={`curso-practicas-${index}`}
                      name="practicas"
                      value={curso.practicas || ''}
                      onChange={(e) => handleCursoChange(e, index)}
                      className={cursoErrors[index]?.practicas ? 'error' : ''}
                      aria-describedby={cursoErrors[index]?.practicas ? `curso-practicas-${index}-error` : undefined}
                    >
                      <option value=""></option>
                      <option value="No">No</option>
                      <option value="Si">Sí</option>
                    </select>
                    {cursoErrors[index]?.practicas && (
                      <span id={`curso-practicas-${index}-error`} className="error-message">
                        {cursoErrors[index]?.practicas}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="button-group">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onNavigateToStart}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onBack}
            >
              Atrás
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSubmitting || isLoading}
            >
              {isSubmitting
                ? (accionFormativaId ? 'Actualizando...' : 'Creando...')
                : (accionFormativaId ? 'Actualizar Acción Formativa' : 'Crear Acción Formativa')
              }
            </button>
          </div>
        </form>

        {/* SnackBar para éxito */}
        <SnackBar
          isOpen={showSuccessSnackbar}
          onClose={() => setShowSuccessSnackbar(false)}
          message={successMessage}
          type={snackbarType}
          duration={4000}
          position="bottom-center"
        />

        {/* Modal para errores */}
        <Modal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          title="Error al Crear Acción Formativa"
          showCloseButton={true}
        >
          <div style={{ textAlign: 'center' }}>
            <p style={{ marginBottom: '20px', color: '#dc3545' }}>
              {errorMessage}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowErrorModal(false)}
            >
              Cerrar
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default AccionFormativaForm;
